import { TableOfContents } from '@/components/TableOfContents';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useT } from '@/i18n/provider';

/**
 * Single sign-on with the customer's own identity provider (ADR-0183):
 * setup per identity provider, Require SSO, break-glass and the error codes.
 * Texts live in i18n (`docs.sso.*`, English and Dutch); other locales fall
 * back to English.
 */

function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24 mb-12">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{title}</h2>
      {children}
    </section>
  );
}

function SubSection({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <div id={id} className="scroll-mt-24 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4">{children}</p>;
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol className="list-decimal pl-6 space-y-2 mb-4 text-gray-600 dark:text-gray-400 leading-relaxed">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ol>
  );
}

function Bullets({ items }: { items: string[] }) {
  return (
    <ul className="list-disc pl-6 space-y-2 mb-4 text-gray-600 dark:text-gray-400 leading-relaxed">
      {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
  );
}

function Callout({ type, children }: { type: 'info' | 'warning'; children: React.ReactNode }) {
  const styles = {
    info: 'border-info-500 bg-info-50 dark:bg-info-500/10 text-info-600 dark:text-info-400',
    warning: 'border-warning-500 bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400',
  };
  return <div className={`my-4 p-4 rounded-xl border-l-4 text-sm leading-relaxed ${styles[type]}`}>{children}</div>;
}

function ErrorTable({ headers, rows }: { headers: [string, string, string]; rows: Array<[string, string, string]> }) {
  return (
    <div className="my-4 overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-800">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 dark:bg-gray-900">
            {headers.map((h) => (
              <th key={h} className="text-left px-4 py-2.5 font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-800">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([code, meaning, action]) => (
            <tr key={code} className="border-b last:border-b-0 border-gray-100 dark:border-gray-800/50 align-top">
              <td className="px-4 py-2.5 font-mono text-xs text-gray-800 dark:text-gray-200 whitespace-nowrap">{code}</td>
              <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">{meaning}</td>
              <td className="px-4 py-2.5 text-gray-600 dark:text-gray-400">{action}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SsoPage() {
  const t = useT();
  const k = (key: string) => t(`docs.sso.${key}`);
  useDocumentMeta({ title: k('meta.title'), description: k('meta.description') });

  const toc = [
    { id: 'how', label: k('toc.how') },
    { id: 'domains', label: k('toc.domains') },
    { id: 'idp', label: k('toc.idp') },
    { id: 'entra', label: k('toc.entra'), level: 1 },
    { id: 'okta', label: k('toc.okta'), level: 1 },
    { id: 'google', label: k('toc.google'), level: 1 },
    { id: 'oidc', label: k('toc.oidc'), level: 1 },
    { id: 'saml', label: k('toc.saml'), level: 1 },
    { id: 'policy', label: k('toc.policy') },
    { id: 'signin', label: k('toc.signin') },
    { id: 'errors', label: k('toc.errors') },
    { id: 'security', label: k('toc.security') },
    { id: 'faq', label: k('toc.faq') },
  ];

  const faq = [1, 2, 3, 4, 5, 6];

  return (
    <div className="flex gap-8">
      <div className="flex-1 min-w-0">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{k('title')}</h1>
        <P>{k('intro')}</P>
        <Callout type="info">{k('availability')}</Callout>

        <Section id="how" title={k('toc.how')}>
          <P>{k('how.p1')}</P>
          <P>{k('how.p2')}</P>
        </Section>

        <Section id="domains" title={k('toc.domains')}>
          <P>{k('domains.p1')}</P>
          <Steps items={[k('domains.step1'), k('domains.step2'), k('domains.step3')]} />
          <P>{k('domains.rules')}</P>
          <Callout type="info">{k('domains.conflict')}</Callout>
        </Section>

        <Section id="idp" title={k('toc.idp')}>
          <P>{k('idp.p1')}</P>
          <P>{k('idp.p2')}</P>

          <SubSection id="entra" title={k('toc.entra')}>
            <Steps items={[1, 2, 3, 4, 5, 6].map((n) => k(`entra.step${n}`))} />
            <Callout type="warning">{k('entra.note')}</Callout>
          </SubSection>

          <SubSection id="okta" title={k('toc.okta')}>
            <Steps items={[1, 2, 3, 4].map((n) => k(`okta.step${n}`))} />
          </SubSection>

          <SubSection id="google" title={k('toc.google')}>
            <Steps items={[1, 2, 3, 4].map((n) => k(`google.step${n}`))} />
          </SubSection>

          <SubSection id="oidc" title={k('toc.oidc')}>
            <P>{k('oidc.p1')}</P>
          </SubSection>

          <SubSection id="saml" title={k('toc.saml')}>
            <P>{k('saml.p1')}</P>
            <Bullets items={[k('saml.acs'), k('saml.entity'), k('saml.metadata')]} />
            <P>{k('saml.p2')}</P>
          </SubSection>

          <P>{k('idp.test')}</P>
        </Section>

        <Section id="policy" title={k('toc.policy')}>
          <Bullets items={[k('policy.jit'), k('policy.outside'), k('policy.breakglass'), k('policy.required')]} />
          <P>{k('policy.requiredPre')}</P>
          <Callout type="warning">{k('policy.locked')}</Callout>
        </Section>

        <Section id="signin" title={k('toc.signin')}>
          <P>{k('signin.p1')}</P>
          <P>{k('signin.p2')}</P>
          <P>{k('signin.p3')}</P>
        </Section>

        <Section id="errors" title={k('toc.errors')}>
          <P>{k('errors.intro')}</P>
          <ErrorTable
            headers={[k('errors.code'), k('errors.meaning'), k('errors.action')]}
            rows={[
              ['sso_required', k('errors.required.m'), k('errors.required.a')],
              ['sso_not_configured', k('errors.notConfigured.m'), k('errors.notConfigured.a')],
              ['sso_email_domain_mismatch', k('errors.mismatch.m'), k('errors.mismatch.a')],
              ['sso_seat_limit', k('errors.seats.m'), k('errors.seats.a')],
              ['sso_outside_member_blocked', k('errors.outside.m'), k('errors.outside.a')],
              ['sso_session_expired', k('errors.expired.m'), k('errors.expired.a')],
              ['account not linked', k('errors.notLinked.m'), k('errors.notLinked.a')],
            ]}
          />
        </Section>

        <Section id="security" title={k('toc.security')}>
          <P>{k('security.p1')}</P>
          <P>{k('security.p2')}</P>
          <P>{k('security.p3')}</P>
        </Section>

        <Section id="faq" title={k('toc.faq')}>
          {faq.map((n) => (
            <div key={n} className="mb-6">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{k(`faq.q${n}`)}</h3>
              <P>{k(`faq.a${n}`)}</P>
            </div>
          ))}
        </Section>
      </div>
      <TableOfContents items={toc} />
    </div>
  );
}
